import { useState, useEffect } from 'react';
import {
  collection,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface ManualAdjustment {
  id: string;
  loanId: string;
  customerName: string | null;
  lender: string | null;
  adjustmentType: 'upfront_commission' | 'trail_commission' | 'clawback' | 'other';
  commissionCents: number;
  gstCents: number | null;
  periodMonth: string;
  settlementDate: string | null;
  loanAmountCents: number | null;
  reason: string;
  createdAt: Date;
  createdBy: string;
  createdByEmail: string;
  voidedAt?: Date | null;
  /** PR C — widened to include 'self_sourced' for 3-tier brokers. v1
   *  manual adjustments don't expose per-leg pct (single introductionPct
   *  field stays); the value is recorded if it flows through legacy paths. */
  introductionType: 'broker' | 'company' | 'self_sourced' | null;
  introductionPct: number | null;
}

interface FirestoreManualAdjustment {
  loanId: string;
  customerName: string | null;
  lender: string | null;
  adjustmentType: 'upfront_commission' | 'trail_commission' | 'clawback' | 'other';
  commissionCents: number;
  gstCents: number | null;
  periodMonth: string;
  settlementDate: string | null;
  loanAmountCents: number | null;
  reason: string;
  createdAt: Timestamp;
  createdBy: string;
  createdByEmail: string;
  voidedAt?: Timestamp | null;
  introductionType?: 'broker' | 'company' | 'self_sourced' | null;
  introductionPct?: number | null;
}

interface UseManualAdjustmentsReturn {
  adjustments: ManualAdjustment[];
  loading: boolean;
  error: string | null;
}

export function useManualAdjustments(
  companyId: string | undefined,
  brokerDocId: string | undefined
): UseManualAdjustmentsReturn {
  const [adjustments, setAdjustments] = useState<ManualAdjustment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId || !brokerDocId) {
      setAdjustments([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const colRef = collection(
      db,
      `companies/${companyId}/brokers/${brokerDocId}/manual_adjustments`
    );

    // No orderBy — avoids potential index requirement on new subcollection.
    // Sort client-side instead.
    const unsubscribe = onSnapshot(
      colRef,
      (snapshot) => {
        const results: ManualAdjustment[] = [];

        snapshot.forEach((docSnap) => {
          const data = docSnap.data() as FirestoreManualAdjustment;
          // Filter out voided adjustments
          if (data.voidedAt) return;
          results.push({
            id: docSnap.id,
            loanId: data.loanId,
            customerName: data.customerName ?? null,
            lender: data.lender ?? null,
            adjustmentType: data.adjustmentType,
            commissionCents: data.commissionCents,
            gstCents: data.gstCents ?? null,
            periodMonth: data.periodMonth,
            settlementDate: data.settlementDate ?? null,
            loanAmountCents: data.loanAmountCents ?? null,
            reason: data.reason,
            createdAt: data.createdAt?.toDate?.() ?? new Date(0),
            createdBy: data.createdBy,
            createdByEmail: data.createdByEmail || 'unknown',
            introductionType: data.introductionType || null,
            introductionPct: data.introductionPct ?? null,
          });
        });

        // Sort by createdAt descending (newest first)
        results.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setAdjustments(results);
        setLoading(false);
      },
      (err) => {
        console.error('[useManualAdjustments] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load adjustments');
        setAdjustments([]);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyId, brokerDocId]);

  return { adjustments, loading, error };
}
